import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {Box, Image, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import CoverSwatchImageNotAvailable from '@amblique/king-living/app/components/cover-swatch-image-not-available';
import {PIM_SQUARE_THUMBNAIL_PX} from '@amblique/king-living/app/components/configuration-selector/utils';

import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';
import {getCoverExtraPrice, getCoverStockMessageClassName, getCoverStockMessageStyleKey, getStockLevel} from '@amblique/king-living/app/utils/cover-stock-display';
import {handleRadioTileKeyDown} from '@amblique/king-living/app/utils/radio-keyboard';

/**
 * Cover variant tile label (KL-973 D8 / R4): catalog-owned variant `name` only.
 * Long names wrap within the tile via line-1 bold styles (`noOfLines={2}`).
 *
 * @param {Object} variant - `c_allVariantsData` entry or SCAPI variant
 * @returns {{ line1: string|null, line2: null, ariaLabel: string }}
 */
export function getCoverVariantLabelLines(variant) {
  const name = (variant?.name ?? '').trim();

  return {
    ariaLabel: name,
    line1: name || null,
    line2: null,
  };
}

/**
 * Cover variant tile component for the cover step.
 * Displays a horizontal card with:
 * - Rectangle thumbnail (76×60px)
 * - Label: variant catalog `name` (bold, wraps in tile width)
 * - Badge: badgeText (if present)
 * - Extra price: variant price - master min price (when > 0)
 * - "View detail" link (mobile only)
 *
 * @param {Object} props.variant - Variant with c_processedData.materialName, swatchName (SFRA display), salePrice, swatchImageUrl
 * @param {boolean} props.isSelected - Whether this variant is selected
 * @param {boolean} props.isMobile - Whether on mobile (shows "View detail" link)
 * @param {string} [props.currency] - Currency code for extra price display
 * @param {number} [props.masterMinPrice] - Master product min price; variant extra price = variant.salePrice - masterMinPrice
 * @param {Object} props.styles - Theme styles
 * @param {Function} props.onSelect - Selection handler
 * @param {Function} [props.onViewDetails] - View details handler (optional, mobile only)
 */
export function CoverVariantTile({
  currency,
  isMobile,
  isSelected,
  masterMinPrice,
  styles,
  tabIndex = 0,
  useSimplePdpCoverStyles = false,
  variant,
  onSelect,
  onViewDetails,
}) {
  const intl = useIntl();
  const currencyCode = currency ?? 'USD';
  const {line1: displayName, ariaLabel: coverAriaLabel} = getCoverVariantLabelLines(variant);
  const badgeText = variant.c_processedData?.badgeText ?? '';
  const imageUrl = variant.swatchImageUrl;

  // Simple PDP cover flyout ("+N more") must display the SAME stock/price indicators as the inline
  // CoverSwatchGrid: tracked-stock variants show In stock / Low stock / Sold out, made-to-order
  // (perpetual) variants show the cover price rule. Reuse the CoverSwatchGrid theme so the badges
  // and message styling match the grid 1:1. The Complex "Customize Your Own" cover step keeps its
  // own price-only layout (useSimplePdpCoverStyles === false).
  const stockLevel = useSimplePdpCoverStyles ? getStockLevel(variant) : null;
  const isPerpetual = stockLevel === 'perpetual';
  const isOutOfStock = stockLevel === 'oos';
  const gridStockStyles = useMultiStyleConfig('CoverSwatchGrid', {isSelected, isOutOfStock});

  let simplePdpStockMessage = null;
  let simplePdpStockMessageStyle = null;
  let simplePdpStockMessageClassName = null;
  let simplePdpStockBadge = null;

  if (useSimplePdpCoverStyles) {
    simplePdpStockMessageStyle = gridStockStyles[getCoverStockMessageStyleKey(stockLevel ?? 'perpetual')];
    simplePdpStockMessageClassName = getCoverStockMessageClassName(stockLevel ?? 'perpetual', {
      variant: 'flyout',
    });

    if (isPerpetual) {
      const coverExtraPrice = getCoverExtraPrice(variant, masterMinPrice);

      simplePdpStockMessage = coverExtraPrice != null ?
        `+ ${formatCurrencyHideTrailingZeroCents(intl, coverExtraPrice, currencyCode)}` :
        intl.formatMessage({defaultMessage: 'Included', id: 'cover_swatch_grid.price.included'});

      if (coverExtraPrice == null) {
        simplePdpStockMessageClassName += ' cover-variant-tile-content-stock-message--included';
      }
    } else if (stockLevel === 'in-stock') {
      simplePdpStockMessage = intl.formatMessage({
        defaultMessage: 'Available',
        id: 'cover_swatch_grid.stock.available',
      });
      simplePdpStockBadge = (
        <Text {...gridStockStyles.tileStockBadge} as="span">
          <FormattedMessage defaultMessage="In stock" id="cover_swatch_grid.badge.in_stock" />
        </Text>
      );
    } else if (stockLevel === 'low-stock') {
      simplePdpStockMessage = intl.formatMessage(
        {
          defaultMessage: 'Only {count, plural, one {# left} other {# left}}',
          id: 'cover_swatch_grid.stock.only_n_left',
        },
        {count: variant.ats ?? 0}
      );
      simplePdpStockBadge = (
        <Text {...gridStockStyles.tileStockBadgeLow} as="span">
          <FormattedMessage defaultMessage="Low stock" id="cover_swatch_grid.badge.low_stock" />
        </Text>
      );
    } else {
      simplePdpStockMessage = intl.formatMessage({
        defaultMessage: 'Sold out',
        id: 'cover_swatch_grid.stock.sold_out',
      });
    }
  }

  // #2 Variant extra price: in each group, compare this variant's price with master min price
  const variantExtraPrice =
    masterMinPrice != null &&
    variant.salePrice != null &&
    variant.salePrice > 0 &&
    (variant.salePrice - masterMinPrice) > 0 ?
      variant.salePrice - masterMinPrice :
      null;

  const handleViewDetails = (e) => {
    e.stopPropagation();
    onViewDetails?.(variant);
  };

  const cardStyles = isSelected ?
    {...styles.coverVariantTile, ...styles.coverVariantTileSelected} :
    styles.coverVariantTile;
  const isDisabled = useSimplePdpCoverStyles && isOutOfStock;
  const handleSelect = () => onSelect(variant);
  const imageWrapperStyles = useSimplePdpCoverStyles ?
    styles.coverVariantTileImageWrapperSimplePdp :
    styles.coverVariantTileImageWrapper;
  const badgeStyles = useSimplePdpCoverStyles ?
    styles.coverVariantTileBadgeSimplePdp :
    styles.coverVariantTileBadge;

  return (
    <Box
      {...cardStyles}
      aria-checked={isSelected}
      aria-disabled={isDisabled || undefined}
      aria-label={coverAriaLabel}
      className="cover-variant-tile"
      role="radio"
      tabIndex={isDisabled ? -1 : tabIndex}
      onClick={() => !isDisabled && handleSelect()}
      onKeyDown={(event) => handleRadioTileKeyDown(event, isDisabled, handleSelect)}
    >
      {/* Thumbnail */}
      <Box
        className="cover-variant-tile-image-wrapper"
        {...imageWrapperStyles}
      >
        {imageUrl ? (
          <Image
            alt={coverAriaLabel}
            htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
            htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
            loading="lazy"
            objectFit="contain"
            src={imageUrl}
            {...styles.coverVariantTileImage}
          />
        ) : (
          <CoverSwatchImageNotAvailable
            svgProps={styles.coverVariantTileImage}
            wrapperProps={imageWrapperStyles}
          />
        )}
        {badgeText ? (
          <Text className="cover-variant-tile-badge" {...badgeStyles}>
            {badgeText}
          </Text>
        ) : (
          simplePdpStockBadge
        )}
        {useSimplePdpCoverStyles && isOutOfStock && (
          <Box
            className="cover-swatch-grid__tile-oos-overlay-line"
            {...gridStockStyles.tileOOSOverlayLine}
          />
        )}
      </Box>

      <Box className="cover-variant-tile-content" {...styles.coverVariantTileContent}>
        {displayName && (
          <Text className="cover-variant-tile-content-name1" noOfLines={2} {...styles.coverVariantTileName1}>
            {displayName}
          </Text>
        )}

        {/*
          Simple PDP cover flyout: single stock-or-price line that matches the inline grid
          (Available / Only N left / Sold out for tracked stock, "+ delta" / "Included" for
          made-to-order). Complex "Customize Your Own" keeps its extra-price + dual-price layout.
        */}
        {useSimplePdpCoverStyles ? (
          simplePdpStockMessage && (
            <Text
              className={simplePdpStockMessageClassName}
              {...simplePdpStockMessageStyle}
            >
              {simplePdpStockMessage}
            </Text>
          )
        ) : (
          <>
            {/* Variant extra price: variant.salePrice - masterMinPrice (when > 0) */}
            {variantExtraPrice != null && variantExtraPrice > 0 && (
              <Text className="cover-variant-tile-extra-price" color={{base: 'brandColors.greys', lg: 'greyscale90Alpha.50'}} fontSize={{base: '13px', lg: '12px'}} marginTop="6px">
                +{' '}
                {formatCurrencyHideTrailingZeroCents(intl, variantExtraPrice, currencyCode)}
              </Text>
            )}

            {/* Dual price: sale + list (strikethrough) when variant has a list price */}
            {variant.price != null && variant.salePrice != null && variant.price > variant.salePrice && (
              <Text className="cover-variant-tile-dual-price" color={{base: 'brandColors.greys', lg: 'greyscale90Alpha.50'}} fontSize={{base: '13px', lg: '12px'}} marginTop="6px">
                {formatCurrencyHideTrailingZeroCents(intl, variant.salePrice, currencyCode)}
                {' '}
                <Text as="span" color="greyscale.70" fontSize="xs" textDecoration="line-through">
                  {formatCurrencyHideTrailingZeroCents(intl, variant.price, currencyCode)}
                </Text>
              </Text>
            )}
          </>
        )}

        {isMobile && onViewDetails && (
          <Text
            className="view-detail-link"
            {...styles.coverVariantTileViewDetails}
            as="span"
            display={{base: 'inline', lg: 'none'}}
            tabIndex={-1}
            onClick={handleViewDetails}
          >
            <FormattedMessage
              defaultMessage="See details"
              id="customize_panel.cover_variant.view_details"
            />
          </Text>
        )}
      </Box>
    </Box>
  );
}

CoverVariantTile.displayName = 'CoverVariantTile';

CoverVariantTile.propTypes = {
  currency: PropTypes.string,
  isMobile: PropTypes.bool,
  isSelected: PropTypes.bool,
  masterMinPrice: PropTypes.number,
  tabIndex: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func,
  styles: PropTypes.object.isRequired,
  useSimplePdpCoverStyles: PropTypes.bool,
  variant: PropTypes.shape({
    productId: PropTypes.string,
    name: PropTypes.string,
    ats: PropTypes.number,
    price: PropTypes.number,
    salePrice: PropTypes.number,
    swatchName: PropTypes.string,
    swatchImageUrl: PropTypes.string,
    variationValues: PropTypes.object,
    c_processedData: PropTypes.shape({
      badgeText: PropTypes.string,
      materialName: PropTypes.string,
    }),
  }).isRequired,
};
